import React, { useCallback, useContext, useEffect } from 'react';
import { EditCustomerContext, IEditCustomerContext } from '../../../editCustomerState/editCustomerContext';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';
import { CardBrands, RequestEntity } from 'types';
import { useEntityPhotoUrl } from 'hooks/useEntityPhotoUrl';
import { Form } from 'antd';
import { getFileIdForSubmit } from 'utils/helpers';
import { FormPage } from 'components/EntityForm/FormPage';
import { FormSection } from 'components/EntityForm/FormSection';
import { FormRow } from 'components/EntityForm/FormRow';
import { FormColumn } from 'components/EntityForm/FormColumn';
import { FormActions } from 'components/EntityForm/FormActions';
import { ImageBlock } from 'components/EntityForm/ImageBlock';
import { IconName } from 'components/Icon/Icon';
import { BillTemplateBlockNew } from 'components/BillTemplateBlock/BillTemplateBlockNew';
import { TipsBlockNew } from 'components/TipsBlock/TipsBlockNew';
import Text from 'components/Text/Text';
import { NameField } from './NameField';
import { EmailField } from './EmailField';
import { EmailBodyTemplateField } from './EmailBodyTemplateField';
import { ClientTypeSelect } from './ClientTypeSelect';
import { PriceTypeField } from './PriceTypeField';
import { VivaWalletCredentialsSelect } from './VivaWalletCredentialsSelect';
import { AdsField } from './AdsField';
import { MenuVersionField } from './MenuVersionField';
import { ContentVersionField } from './ContentVersionField';
import { DefaultLanguageSelect } from './DefaultLanguageSelect';
import { LanguagesMultipleSelect } from './LanguagesMultipleSelect';
import { ShowLoyaltyProgramButtonsCheckBox } from './ShowLoyaltyProgramButtonsCheckBox';
import { Mcc } from './Mcc';
import { Tags } from './Tags';
import { StopListSyncIntervalField } from './StopListSyncIntervalField';
import { ActivateBPACheckBox } from './ActivateBPACheckBox';
import { MixpanelTokenField } from './MixpanelTokenField';
import classNames from 'classnames';

interface IProps {
	isVisible: boolean;
}

export const MainInfoForm: React.FC<IProps> = ({ isVisible }) => {
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		setFile,
		deletePhoto,
		changeName,
		changeEmail,
		changeEmailBodyTemplate,
		setClientType,
		changeBillTemplateFileData,
		changeBillAdvertisingImageData,
		changeBillAdvertisingLink,
		deleteBillAdvertisingImage,
		changePriceType,
		setTips0,
		setTips1,
		setTips2,
		setTips3,
		setDefaultTips,
		toggleShowLoyaltyProgramButtons,
		addMccCard,
		deleteMccCard,
		changeBaseMcc,
		changeMainMccCardBrand,
		changeMccCardBrand,
		changeMainMccCardMcc,
		changeMccCardMcc,
		setVivaWalletCredentialsId,
		setAds,
		changeMenuVersion,
		changeContentVersion,
		setMixpanelToken,
		setStopListSyncIntervalMinutes,
		setCustomerDefaultLanguage,
		addTags,
		changeTags,
		deleteTag,
		setCustomerLanguages,
		setFetchedLanguages,
		toggleActivateBPA,
		customerId,
		filePath,
		fileId,
		sourceUrlLocally,
		name,
		email,
		emailBodyTemplate,
		clientType,
		billTemplateFileData,
		billTemplateFilePath,
		billAdvertisingImageData,
		billAdvertisingImage,
		billAdvertisingLink,
		priceType,
		tips0,
		tips1,
		tips2,
		tips3,
		defaultTips,
		isShowLoyaltyProgramButtons,
		baseMcc,
		mainMccCardBrand,
		mainMccCardMcc,
		mccCards,
		vivaWalletCredentialsId,
		ads,
		menuVersion,
		contentVersion,
		mixpanelToken,
		stopListSyncIntervalMinutes,
		IDSelectedCustomerDefaultLanguage,
		IDsSelectedCustomerLanguages,
		tags,
		isActiveBpa,
	} = useContext<IEditCustomerContext>(EditCustomerContext);
	const [form] = Form.useForm();
	const { photoUrl, isPhotoError } = useEntityPhotoUrl({
		resource: RequestEntity.Customers,
		entityId: customerId,
		filePath,
	});

	const reservedMccCardBrands: Nullable<CardBrands>[] = [mainMccCardBrand, ...mccCards.map((el) => el.brand)];

	useEffect(() => {
		form.setFieldsValue({
			name,
			email,
			emailBodyTemplate,
			clientType,
			billAdvertisingLink,
			priceType,
			tips0,
			tips1,
			tips2,
			tips3,
			baseMcc,
			mainMccCardBrand,
			mainMccCardMcc,
			vivaWalletCredentialsId,
			ads,
			menuVersion,
			contentVersion,
			mixpanelToken,
			stopListSyncIntervalMinutes,
			IDSelectedCustomerDefaultLanguage,
			IDsSelectedCustomerLanguages,
		});
		mccCards.forEach((el) => {
			form.setFieldsValue({ [`cardBrand-${el.id}`]: el.brand, [`mcc-${el.id}`]: el.mcc });
		});
	}, [
		name,
		email,
		emailBodyTemplate,
		clientType,
		billAdvertisingLink,
		priceType,
		tips0,
		tips1,
		tips2,
		tips3,
		baseMcc,
		mainMccCardBrand,
		mainMccCardMcc,
		mccCards,
		vivaWalletCredentialsId,
		ads,
		menuVersion,
		contentVersion,
		mixpanelToken,
		stopListSyncIntervalMinutes,
		IDSelectedCustomerDefaultLanguage,
		IDsSelectedCustomerLanguages,
	]);

	const submit = () => {
		setMainFormValidationFail(false);

		const mccCardsForSubmit = [
			{ brand: mainMccCardBrand, mcc: mainMccCardMcc },
			...mccCards.map((el) => ({ brand: el.brand, mcc: el.mcc })),
		].filter((el) => el.brand && el.mcc);

		setDataForSubmit({
			id: customerId,
			name,
			fileId: getFileIdForSubmit({ fileId, filePath }),
			email,
			emailBodyTemplate,
			clientType: clientType || 1,
			billTemplateId: getFileIdForSubmit({
				fileId: billTemplateFileData.fileId,
				filePath: billTemplateFilePath,
			}),
			billAdvertisingImage: getFileIdForSubmit({
				fileId: billAdvertisingImageData.fileId,
				filePath: billAdvertisingImage,
			}),
			billAdvertisingLink,
			priceType,
			defaultTips: [
				{ value: tips0, isDefault: defaultTips === '0' },
				{ value: tips1, isDefault: defaultTips === '1' },
				{ value: tips2, isDefault: defaultTips === '2' },
				{ value: tips3, isDefault: defaultTips === '3' },
			],
			isShowLoyaltyProgramButtons,
			vivaWalletCredentialsId,
			ads: Number(ads),
			menuVersion: menuVersion || null,
			contentVersion: contentVersion || null,
			mixpanelToken: mixpanelToken || null,
			stopListSyncIntervalMinutes: stopListSyncIntervalMinutes || 0,
			defaultLanguageId: IDSelectedCustomerDefaultLanguage || '',
			availableLanguageIds: IDsSelectedCustomerLanguages,
			tags,
			merchantCategoryCode: {
				baseMcc: baseMcc || 0,
				mccCards: mccCardsForSubmit,
			},
			isActiveBpa,
		});

		if (additionalValidationErrors.length) {
			setMainFormValidationFail(true);
		} else {
			changeTypeOfModalWindow('toConfirmSubmit');
		}
	};

	const handleFormValidationFail = () => {
		setMainFormValidationFail(true);
	};

	const handleCancel = () => {
		changeTypeOfModalWindow('toConfirmFieldsClearing');
	};

	const handleUploadPhoto = ({ fileId, imageSrc }: { fileId: string; imageSrc: string }) => {
		setFile(fileId, imageSrc);
	};

	const handleInvalidImageFormat = () => {
		changeTypeOfModalWindow('invalidFileFormat');
	};

	const handleInvalidHtmlFormat = () => {
		changeTypeOfModalWindow('invalidHtmlFileFormat');
	};

	const handleFileUploadError = () => {
		changeTypeOfModalWindow('fileUploadError');
	};

	const handleTooManyFiles = () => {
		changeTypeOfModalWindow('tooManyFiles');
	};

	const handleFileDownloadError = () => {
		changeTypeOfModalWindow('fileDownloadError');
	};

	const handleImageFileSizeExceeded = useCallback(() => changeTypeOfModalWindow('imageFileSizeExceeded'), []);
	const handleHtmlFileSizeExceeded = useCallback(() => changeTypeOfModalWindow('htmlFileSizeExceeded'), []);

	return (
		<div className={classNames({ hidden: !isVisible })} data-test="EditForm">
			<FormPage form={form} testId="Form" onFinish={submit} onFinishFailed={handleFormValidationFail}>
				<FormSection translationKey="mainInfoHeading" testId="MainInfoSection">
					<FormRow>
						<ImageBlock
							imageSrc={sourceUrlLocally || photoUrl}
							isImageError={!sourceUrlLocally && isPhotoError}
							placeholderIcon={IconName.user}
							maxFileSizeMB={1}
							onUpload={handleUploadPhoto}
							onDelete={deletePhoto}
							onSizeExceeded={handleImageFileSizeExceeded}
							onInvalidFormat={handleInvalidImageFormat}
							onUploadError={handleFileUploadError}
						/>
						<FormColumn>
							<NameField changeName={changeName} />
							<EmailBodyTemplateField changeEmailBodyTemplate={changeEmailBodyTemplate} />
						</FormColumn>
						<FormColumn>
							<EmailField changeEmail={changeEmail} />
							<ClientTypeSelect setClientType={setClientType} />
						</FormColumn>
					</FormRow>
				</FormSection>
				<FormSection translationKey="billTemplateAndAdImageHeading" testId="BillTemplateSection">
					<BillTemplateBlockNew
						billTemplateFileData={billTemplateFileData}
						billAdvertisingImageData={billAdvertisingImageData}
						billTemplateFilePath={billTemplateFilePath}
						billAdvertisingImage={billAdvertisingImage}
						changeBillTemplateFileData={changeBillTemplateFileData}
						changeBillAdvertisingImageData={changeBillAdvertisingImageData}
						changeBillAdvertisingLink={changeBillAdvertisingLink}
						onDeleteBillAdvertisingImage={deleteBillAdvertisingImage}
						onHtmlFileSizeExceeded={handleHtmlFileSizeExceeded}
						onImageFileSizeExceeded={handleImageFileSizeExceeded}
						onInvalidHtmlFormat={handleInvalidHtmlFormat}
						onInvalidImageFormat={handleInvalidImageFormat}
						onUploadError={handleFileUploadError}
						onTooManyFiles={handleTooManyFiles}
						onDownloadError={handleFileDownloadError}
					/>
				</FormSection>
				<FormSection translationKey="priceTypeHeading" testId="PriceTypeSection">
					<PriceTypeField changePriceType={changePriceType} />
				</FormSection>
				<FormSection translationKey="tipsHeading" testId="TipsSection">
					<TipsBlockNew
						setTips0={setTips0}
						setTips1={setTips1}
						setTips2={setTips2}
						setTips3={setTips3}
						setDefaultTips={setDefaultTips}
						defaultTips={defaultTips}
					/>
				</FormSection>
				<FormSection translationKey="loyaltyProgramHeading" testId="LoyaltyProgramSection">
					<ShowLoyaltyProgramButtonsCheckBox
						toggleShowLoyaltyProgramButtons={toggleShowLoyaltyProgramButtons}
						isShowLoyaltyProgramButtons={isShowLoyaltyProgramButtons}
					/>
				</FormSection>
				<FormSection translationKey="mccHeading" testId="MccSection">
					<Mcc
						mccCards={mccCards}
						mainMccCardBrand={mainMccCardBrand}
						reservedMccCardBrands={reservedMccCardBrands}
						changeBaseMcc={changeBaseMcc}
						changeMainMccCardBrand={changeMainMccCardBrand}
						changeMainMccCardMcc={changeMainMccCardMcc}
						changeMccCardBrand={changeMccCardBrand}
						changeMccCardMcc={changeMccCardMcc}
						addMccCard={addMccCard}
						deleteMccCard={deleteMccCard}
					/>
				</FormSection>
				<FormSection translationKey="tagsForSearchInMenuHeading" testId="TagsSection">
					<Tags
						addTags={addTags}
						changeTags={changeTags}
						deleteTag={deleteTag}
						tags={tags}
						IDSelectedCustomerDefaultLanguage={IDSelectedCustomerDefaultLanguage}
						IDsSelectedCustomerLanguages={IDsSelectedCustomerLanguages}
					/>
				</FormSection>
				<FormSection translationKey="credentialsNameHeading" testId="CredentialsSection">
					<VivaWalletCredentialsSelect setVivaWalletCredentialsId={setVivaWalletCredentialsId} />
				</FormSection>
				<FormSection translationKey="adsHeading" testId="AdsSection">
					<AdsField setAds={setAds} />
				</FormSection>
				<FormSection translationKey="menuContentVersionHeading" testId="MenuContentVersionSection">
					<FormRow>
						<MenuVersionField changeMenuVersion={changeMenuVersion} />
						<ContentVersionField changeContentVersion={changeContentVersion} />
					</FormRow>
				</FormSection>
				<FormSection translationKey="languagesHeading" testId="LanguagesSection">
					<FormRow>
						<DefaultLanguageSelect
							setCustomerDefaultLanguage={setCustomerDefaultLanguage}
							setFetchedLanguages={setFetchedLanguages}
						/>
						<LanguagesMultipleSelect
							IDSelectedCustomerDefaultLanguage={IDSelectedCustomerDefaultLanguage}
							IDsSelectedCustomerLanguages={IDsSelectedCustomerLanguages}
							setCustomerLanguages={setCustomerLanguages}
						/>
					</FormRow>
				</FormSection>
				<FormSection translationKey="enableStopListHeading" testId="StopListSection">
					<StopListSyncIntervalField setStopListSyncIntervalMinutes={setStopListSyncIntervalMinutes} />
				</FormSection>
				<FormSection translationKey="bpaHeading" testId="BpaSection">
					<div className="flex flex-col gap-2">
						<ActivateBPACheckBox toggleActivateBPA={toggleActivateBPA} isActiveBpa={isActiveBpa} />
						<Text
							translationNameSpace="form"
							translationKey="BPAIsUsedOnlyByThoseUsersWhoHaveEnteredIntoAgreementToWorkUnderBPAScheme"
							size="xs"
							theme="neutralMedium"
						/>
					</div>
				</FormSection>
				<FormSection translationKey="mixpanelHeading" testId="MixpanelSection">
					<MixpanelTokenField setMixpanelToken={setMixpanelToken} />
				</FormSection>
				<FormActions onCancel={handleCancel} />
			</FormPage>
		</div>
	);
};
